import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { isEmpty } from 'lodash-es';

import { litStyles as buttonStyles } from 'portal-base/src/base/css/ki-button-styles.css';
import i18n from 'portal-base/src/decorators/i18n';

import { dsViewStyles } from 'ds-shared-lib/src/base/css/ki-ds-view-styles.css';

import nls from '../../locales/indexes.nls';
import { getAlarmData } from '../../services/alarming';
import {
    fetchChannelMetaData,
    fetchLastValues,
    fetchTimeseriesMeta,
    getChannelIdFromUrl,
    getTimeseriesIdsFromUrl,
    groupByLocation,
} from '../../services/channel';
import { isMobile, isStandalone, mobileQuery } from '../../services/device';
import { getParameters } from '../../services/parameters';
import { showToast } from '../../services/toast-service';
import { setUrlParameter } from '../../utils/setUrlParameter';
import KiDsPwaView from '../pwa-view/ki-ds-pwa-view';
import styles from './combined-view.css';
import '../../components/ki-ds-pwa-about/ki-ds-pwa-about';
import '../../components/ki-ds-pwa-install-prompt/ki-ds-pwa-install-prompt';
import '../../components/ki-ds-pwa-alarm-status/ki-ds-pwa-alarm-status';
import '../../components/ki-ds-pwa-timeseries-list/ki-ds-pwa-timeseries-list';
import '../../components/ki-ds-pwa-graph-container/ki-ds-pwa-graph-container';

const CLASS_NAME = 'ki-ds-pwa-combined-view';

@i18n(nls)
@customElement(CLASS_NAME)
class KiDsCombinedView extends KiDsPwaView {
    static styles = [...dsViewStyles, buttonStyles, styles];

    static get properties() {
        return {
            channel: { type: Object },
            timeseriesByLocation: { type: Object },
            alarmStatus: { type: Array },
            alarmConfigs: { type: Array },
            classificationTemplate: { type: Object },
            selectedTimeseries: { type: Array },
            isLoading: { type: Boolean },
            isGraphOpen: { type: Boolean },
            parameters: { type: Object },
            _multiSelectMode: { type: Boolean, state: true },
            _installPrompt: { type: Object, state: true },
        };
    }

    constructor() {
        super();
        this.channel = null;
        this.timeseriesByLocation = {};
        this.alarmStatus = [];
        this.alarmConfigs = [];
        this.classificationTemplate = null;
        this.selectedTimeseries = [];
        this.isLoading = true;
        this._multiSelectMode = false;
        this.isGraphOpen = false;
        this._installPrompt = null;
        this._onViewportChange = () => this.requestUpdate();
        this._onBeforeInstallPrompt = (e) => {
            e.preventDefault();
            this._installPrompt = e;
        };
    }

    connectedCallback() {
        super.connectedCallback();
        mobileQuery.addEventListener('change', this._onViewportChange);
        window.addEventListener('beforeinstallprompt', this._onBeforeInstallPrompt);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        mobileQuery.removeEventListener('change', this._onViewportChange);
        window.removeEventListener('beforeinstallprompt', this._onBeforeInstallPrompt);
    }

    async firstUpdated() {
        super.firstUpdated();
        await this._loadView();
    }

    async _loadView() {
        this.isLoading = true;
        try {
            this.channelId = getChannelIdFromUrl();

            const [parameters, channel, timeseriesMeta, lastValues] = await Promise.all([
                getParameters(),
                fetchChannelMetaData(this.channelId),
                fetchTimeseriesMeta(this.channelId),
                fetchLastValues(this.channelId),
            ]);

            this.parameters = parameters;
            this.channel = channel;
            this.timeseriesByLocation = groupByLocation(timeseriesMeta, lastValues);
            this._setDocumentTitle(channel);
            this._dispatchChannelLoaded(channel);
            this._applyUrlPreselection();

            if (channel.channelFeatures?.alarming) {
                await this._loadAlarmData();
            }
        } catch (_err) {
            showToast({ content: 'Failed to load channel data', type: 'error' });
        } finally {
            this.isLoading = false;
        }
    }

    async _loadAlarmData() {
        const alarmData = await getAlarmData(this.channelId);
        this.alarmStatus = alarmData.alarmStatus;
        this.alarmConfigs = alarmData.alarmConfigs;
        this.classificationTemplate = alarmData.classificationTemplate;
    }

    __toggleAddTimeseries(ts) {
        const isSelected = this.selectedTimeseries.some((s) => s.id === ts.id);
        this._applyMultiSelection(
            isSelected
                ? this.selectedTimeseries.filter((s) => s.id !== ts.id)
                : [...this.selectedTimeseries, ts],
        );
    }

    __deselectTimeseries(tsId) {
        this._applyMultiSelection(this.selectedTimeseries.filter((ts) => ts.id !== tsId));
    }

    _applyMultiSelection(selection) {
        this.selectedTimeseries = selection;
        this._multiSelectMode = !isEmpty(selection);
        if (!this._isMobile) {
            this.isGraphOpen = !isEmpty(selection);
        } else if (isEmpty(selection)) {
            this.isGraphOpen = false;
        }
        this._syncTsIdParam();
    }

    __toggleTimeseries(ts) {
        if (this._isMobile && this._multiSelectMode) {
            this.__toggleAddTimeseries(ts);
            return;
        }
        const isSelected =
            this.selectedTimeseries.length === 1 && this.selectedTimeseries[0].id === ts.id;
        this.selectedTimeseries = isSelected ? [] : [ts];
        this._multiSelectMode = false;
        this.isGraphOpen = !isEmpty(this.selectedTimeseries);
        this._syncTsIdParam();
    }

    __clearSelection() {
        this.selectedTimeseries = [];
        this._multiSelectMode = false;
        this.isGraphOpen = false;
        this._syncTsIdParam();
    }

    _applyUrlPreselection() {
        const presetTsIds = getTimeseriesIdsFromUrl();
        if (isEmpty(presetTsIds)) {
            return;
        }

        const allTimeseries = Object.values(this.timeseriesByLocation).flat();
        this.selectedTimeseries = allTimeseries.filter((ts) => presetTsIds.includes(ts.id));
        this._multiSelectMode = this.selectedTimeseries.length > 1;
        this.isGraphOpen = !isEmpty(this.selectedTimeseries);

        // drop IDs from the URL that no longer exist in the channel
        if (this.selectedTimeseries.length !== presetTsIds.length) {
            this._syncTsIdParam();
        }
    }

    _syncTsIdParam() {
        setUrlParameter('tsId', this._selectedIds);
    }

    get _isMobile() {
        return isMobile();
    }

    get _graphActive() {
        return !this._isMobile || this.isGraphOpen;
    }

    get _showInstallPrompt() {
        return this._installPrompt && this._isMobile && !isStandalone();
    }

    _hideInstallPrompt() {
        this._installPrompt = null;
    }

    get _selectedIds() {
        return this.selectedTimeseries.map((ts) => ts.id);
    }

    render() {
        return html`
            <div class="view">
                <div class="main">
                    <div class="sidebar">
                        ${this._showInstallPrompt
                            ? html`<ki-ds-pwa-install-prompt
                                            .deferredPrompt=${this._installPrompt}
                                            @install-accepted=${this._hideInstallPrompt}
                                            @install-dismissed=${this._hideInstallPrompt}
                                        ></ki-ds-pwa-install-prompt>`
                            : nothing}
                        ${this.channel?.description
                            ? html`<div class="sidebar-item">
                                  <ki-ds-pwa-about
                                      .isLoading=${this.isLoading}
                                      .name=${this.channel?.name}
                                      .description=${this.channel?.description}
                                  ></ki-ds-pwa-about>
                              </div>`
                            : nothing}
                        ${this.isLoading || !isEmpty(this.alarmStatus)
                ? html` <div class="sidebar-item">
                                  <ki-ds-pwa-alarm-status
                                      .statusList=${this.alarmStatus}
                                      .isLoading=${this.isLoading}
                                  ></ki-ds-pwa-alarm-status>
                              </div>`
                : nothing}
                        <div class="sidebar-item sidebar-item-fill">
                            <ki-ds-pwa-timeseries-list
                                .timeseriesByLocation=${this.timeseriesByLocation}
                                .selectedIds=${this._selectedIds}
                                .alarmStatusList=${this.alarmStatus}
                                .alarmConfigs=${this.alarmConfigs}
                                .classificationTemplate=${this.classificationTemplate}
                                .parameters=${this.parameters}
                                .isLoading=${this.isLoading}
                                @ts-select=${(e) => this.__toggleTimeseries(e.detail.timeseries)}
                                @ts-add=${(e) => this.__toggleAddTimeseries(e.detail.timeseries)}
                                @clear-selection=${this.__clearSelection}
                            ></ki-ds-pwa-timeseries-list>
                        </div>
                    </div>
                    <div class="content ${this.isGraphOpen ? 'content-open' : ''}">
                        <div class="mobile-graph-header">
                            <span class="mobile-graph-title"
                                >${this.selectedTimeseries.map((ts) => ts.name).join(', ')}</span
                            >
                            <button
                                class="icon-button small primary close-btn"
                                @click=${this.__closeMobileGraph}
                            >
                                <i class="fa-sharp fa-solid fa-times"></i>
                            </button>
                        </div>
                        <div class="content-body">
                            <ki-ds-pwa-graph-container
                                .tsIdList=${this._graphActive ? this._selectedIds : []}
                                .currentMetaList=${this._graphActive ? this.selectedTimeseries : []}
                                .channelId=${this.channelId}
                                .isLoading=${this.isLoading}
                                @removeTimeSeries=${(e) =>
                                    this.__deselectTimeseries(e.detail.ts_path)}
                            ></ki-ds-pwa-graph-container>
                        </div>
                    </div>
                </div>
            </div>

            <div
                class="fab-container"
                ?hidden=${isEmpty(this.selectedTimeseries) || this.isGraphOpen}
            >
                <button class="primary" @click=${this.__openMobileGraph}>
                    <i class="hmtmobile hmtmobile-chart-column"></i>
                    Show Graph
                </button>
            </div>
        `;
    }

    __openMobileGraph() {
        this.isGraphOpen = true;
    }

    __closeMobileGraph() {
        this.isGraphOpen = false;
    }
}
